import { FC, useCallback, useMemo } from "react";
import { Button, Divider } from "@mui/material";
import Stack from "@mui/material/Stack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { filterTreeFiltersAreInvalid } from "@/components/pages/common/filters-tree/utils/helpers/filterTreeFiltersAreInvalid";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { dimensionsDataSelector } from "@/store/dimensions/selectors/dimensions-data/dimensionsDataSelector";
import { BillingExplorerFilterTreeAddToDimensionPopoverItem } from "@/components/pages/resource-explorer/components/app-bar/components/resource-explorer-toolbar/components/add-filter-tree-to-dimension/BillingExplorerFilterTreeAddToDimensionPopoverItem";
import { DimensionWithCosts } from "@/services/cloudchipr.api";
import { addBillingExplorerFilterTreeToDimensionThunk } from "@/store/resource-explorer/thunks/filters/addBillingExplorerFilterTreeToDimensionThunk";
import { isTreeNodeFilterGroup } from "@/components/pages/common/filters-tree/utils/helpers/isTreeNodeWithIdFilterGroup";

interface BillingExplorerFilterTreeAddToDimensionPopoverProps {
  filterTree?: FilterTreeNodeWithId;
  onClose(): void;
}

export const BillingExplorerFilterTreeAddToDimensionPopover: FC<
  BillingExplorerFilterTreeAddToDimensionPopoverProps
> = ({ filterTree, onClose }) => {
  const dispatch = useAppDispatch();
  const dimensions = useAppSelector(dimensionsDataSelector);

  const invalid = useMemo(() => {
    return filterTree && filterTreeFiltersAreInvalid(filterTree);
  }, [filterTree]);

  const dimensionSelectHandler = useCallback(
    (dimension: DimensionWithCosts) => {
      if (!filterTree) {
        return;
      }
      dispatch(
        addBillingExplorerFilterTreeToDimensionThunk({
          dimensionId: dimension.id,
          dimensionName: dimension.name,
          filterTree,
          drawerMode: "dimensionEdit",
          categories: dimension.categories,
        }),
      );
      onClose();
    },
    [dispatch, filterTree, onClose],
  );

  const addDimensionClickHandler = useCallback(() => {
    if (!filterTree) {
      return;
    }

    dispatch(
      addBillingExplorerFilterTreeToDimensionThunk({
        filterTree,
        drawerMode: "dimensionCreate",
        dimensionName: "New Dimension",
      }),
    );
    onClose();
  }, [dispatch, filterTree, onClose]);

  const disabled =
    invalid ||
    !filterTree ||
    (isTreeNodeFilterGroup(filterTree) && !filterTree?.items?.length);

  return (
    <Stack>
      <Button
        variant="text"
        size="small"
        endIcon={<OpenInNewIcon color="action" />}
        component={Link}
        target="_blank"
        to="/dimensions"
        sx={{
          justifyContent: "flex-start",
          pl: 2,
          py: 1,
          color: "text.secondary",
        }}
      >
        DIMENSIONS
      </Button>
      <Divider />

      <Stack maxHeight={400} overflow="auto">
        {dimensions?.map((item) => {
          return (
            <BillingExplorerFilterTreeAddToDimensionPopoverItem
              key={item.id}
              {...item}
              onClick={dimensionSelectHandler}
            />
          );
        })}
      </Stack>

      <Button
        onClick={addDimensionClickHandler}
        variant="text"
        size="small"
        startIcon={
          <AddIcon color={disabled ? "disabled" : "primary"} fontSize="small" />
        }
        disabled={disabled}
        sx={{
          px: 2,
          py: 1,
        }}
        fullWidth
      >
        Create new dimension
      </Button>
    </Stack>
  );
};
